import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { clsx } from "clsx";
import { twRing } from "@/lib/emotionTw";

export type ColorPickerSize = "xs" | "sm" | "md" | "lg";

export type ColorPickerProps = Omit<InputHTMLAttributes<HTMLInputElement>, "defaultValue" | "onChange" | "size" | "type" | "value"> & {
  value: string;
  onValueChange: (value: string) => void;
  label?: ReactNode;
  helperText?: ReactNode;
  errorText?: ReactNode;
  disabled?: boolean;
  size?: ColorPickerSize;
  containerClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  swatchClassName?: string;
};

const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

/* Styles are the exact compiled output of the pre-migration Tailwind
 * utilities, emitted in the original class-list order. Verified against the
 * frozen baseline build — do not "clean up" values. */

const ColorPickerRoot = styled("div", {
  label: "AgenticColorPicker-root"
})({
  width: "100%"
});

const ColorPickerLabel = styled("label", {
  label: "AgenticColorPicker-label",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { disabled: boolean } }>(({ ownerState }) => [
  {
    marginBottom: ".375rem",
    display: "block",
    fontSize: "12px",
    fontWeight: 600,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  },
  ownerState.disabled && {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
  }
]);

const cpFieldBase: CSSObject = {
  display: "flex",
  width: "100%",
  alignItems: "center",
  gap: ".5rem",
  borderRadius: "var(--bfrs-input-radius)",
  borderWidth: "1px",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  paddingLeft: ".5rem",
  paddingRight: ".5rem",
  transitionProperty:
    "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
  transitionDuration: ".15s"
};

const cpFieldSize: Record<ColorPickerSize, CSSObject> = {
  xs: { height: "29px", fontSize: "12.5px" },
  sm: { height: "2rem", fontSize: ".75rem", lineHeight: "1rem" },
  md: { height: "34px", fontSize: "13px" },
  lg: { height: "2.5rem", fontSize: ".875rem", lineHeight: "1.25rem" }
};

/* In the invalid branch tailwind-merge dropped the base
 * `focus-within:bfrs-border-border-focus` and the base
 * `focus-within:bfrs-ring-primary-soft` (same ring-color group as the later
 * `focus-within:bfrs-ring-danger/15`), so `--tw-ring-opacity` is only set in
 * the non-invalid state — modeled by keeping the whole focus-within ring in
 * the branches below (`bfrs-ring-primary-soft` is a full-opacity ring color). */
const cpFieldInvalid: CSSObject = {
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))",
  "&:focus-within": {
    borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))",
    ...twRing("2px", "hsl(var(--bfrs-color-danger) / .15)")
  }
};

const cpFieldDefaultBorder: CSSObject = {
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  "&:focus-within": {
    borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))",
    ...twRing("2px", "hsl(var(--bfrs-color-primary-soft) / var(--tw-ring-opacity, 1))"),
    "--tw-ring-opacity": "1"
  }
};

const cpFieldDisabled: CSSObject = {
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
};

interface ColorPickerFieldOwnerState {
  size: ColorPickerSize;
  invalid: boolean;
  disabled: boolean;
}

const ColorPickerField = styled("div", {
  label: "AgenticColorPicker-field",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: ColorPickerFieldOwnerState }>(({ ownerState }) => [
  cpFieldBase,
  cpFieldSize[ownerState.size],
  ownerState.invalid ? cpFieldInvalid : cpFieldDefaultBorder,
  ownerState.disabled && cpFieldDisabled
]);

const cpSwatchSize: Record<ColorPickerSize, CSSObject> = {
  xs: { width: "1.25rem", height: "1.25rem" },
  sm: { width: "1.25rem", height: "1.25rem" },
  md: { width: "1.5rem", height: "1.5rem" },
  lg: { width: "1.75rem", height: "1.75rem" }
};

const ColorPickerSwatchInput = styled("input", {
  label: "AgenticColorPicker-swatchInput",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { size: ColorPickerSize } }>(({ ownerState }) => [
  {
    flexShrink: 0,
    cursor: "pointer",
    borderRadius: "var(--bfrs-radius-md)",
    borderWidth: "1px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
    backgroundColor: "transparent",
    padding: "0",
    outline: "2px solid transparent",
    outlineOffset: "2px",
    "&:disabled": {
      cursor: "not-allowed",
      opacity: ".5"
    },
    "&::-moz-color-swatch": {
      borderRadius: "5px",
      borderWidth: "0px"
    },
    "&::-webkit-color-swatch-wrapper": {
      padding: "0"
    },
    "&::-webkit-color-swatch": {
      borderRadius: "5px",
      borderWidth: "0px"
    }
  },
  cpSwatchSize[ownerState.size]
]);

const ColorPickerHexInput = styled("input", {
  label: "AgenticColorPicker-hexInput"
})({
  minWidth: "0px",
  flex: "1 1 0%",
  borderWidth: "0px",
  backgroundColor: "transparent",
  fontFamily: "ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,Liberation Mono,Courier New,monospace",
  textTransform: "uppercase",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
  outline: "2px solid transparent",
  outlineOffset: "2px",
  "&::-moz-placeholder": {
    "--tw-text-opacity": "1",
    color: "rgb(var(--bfrs-color-text-placeholder) / var(--tw-text-opacity, 1))"
  },
  "&::placeholder": {
    "--tw-text-opacity": "1",
    color: "rgb(var(--bfrs-color-text-placeholder) / var(--tw-text-opacity, 1))"
  },
  "&:disabled::-moz-placeholder": {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
  },
  "&:disabled::placeholder": {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
  },
  "&:disabled": {
    cursor: "not-allowed",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
  }
});

/* `bfrs-text-xs bfrs-leading-5`: both kept; `.bfrs-leading-5` sits later in the
 * compiled stylesheet than `.bfrs-text-xs`, so line-height 1.25rem wins. */
const cpMessageBase: CSSObject = {
  marginTop: ".375rem",
  fontSize: ".75rem",
  lineHeight: "1.25rem"
};

const ColorPickerError = styled("p", {
  label: "AgenticColorPicker-error"
})({
  ...cpMessageBase,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-danger) / var(--tw-text-opacity, 1))"
});

const ColorPickerHelper = styled("p", {
  label: "AgenticColorPicker-helper"
})({
  ...cpMessageBase,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const normalizeColorInputValue = (value: string) => (HEX_COLOR_PATTERN.test(value) ? value : "#000000");

export const ColorPicker = forwardRef<HTMLInputElement, ColorPickerProps>(
  (
    {
      className,
      containerClassName,
      labelClassName,
      inputClassName,
      swatchClassName,
      id,
      value,
      onValueChange,
      label,
      helperText,
      errorText,
      disabled,
      size = "md",
      "aria-describedby": ariaDescribedBy,
      "aria-invalid": ariaInvalid,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const helperId = helperText ? `${inputId}-helper` : undefined;
    const errorId = errorText ? `${inputId}-error` : undefined;
    const describedBy = [ariaDescribedBy, helperId, errorId].filter(Boolean).join(" ") || undefined;
    const invalid = Boolean(errorText);

    return (
      <ColorPickerRoot className={clsx(containerClassName, className) || undefined}>
        {label && (
          <ColorPickerLabel htmlFor={inputId} ownerState={{ disabled: Boolean(disabled) }} className={labelClassName}>
            {label}
          </ColorPickerLabel>
        )}
        <ColorPickerField ownerState={{ size, invalid, disabled: Boolean(disabled) }}>
          <ColorPickerSwatchInput
            ref={ref}
            id={inputId}
            type="color"
            value={normalizeColorInputValue(value)}
            disabled={disabled}
            aria-describedby={describedBy}
            aria-invalid={ariaInvalid ?? invalid}
            ownerState={{ size }}
            className={swatchClassName}
            onChange={(event) => onValueChange(event.currentTarget.value)}
            {...props}
          />
          <ColorPickerHexInput
            type="text"
            value={value}
            disabled={disabled}
            aria-label={typeof label === "string" ? `${label} hex value` : "Hex color value"}
            aria-describedby={describedBy}
            aria-invalid={ariaInvalid ?? invalid}
            className={inputClassName}
            spellCheck={false}
            onChange={(event) => onValueChange(event.currentTarget.value)}
          />
        </ColorPickerField>
        {errorText ? (
          <ColorPickerError id={errorId}>{errorText}</ColorPickerError>
        ) : helperText ? (
          <ColorPickerHelper id={helperId}>{helperText}</ColorPickerHelper>
        ) : null}
      </ColorPickerRoot>
    );
  }
);

ColorPicker.displayName = "ColorPicker";
